import { FileUpload, FormInput, FormSelect, Patterns } from '@common/components/forms';
import { CustomDatePicker, spreadAndtransformDefaultValues } from '@common/components/forms/controls';
import { ClientConfigurationModel, ILicenseModel } from '@common/models';
import { ErrorMessage } from '@hookform/error-message';
import { ILicenseModelWithNewLicenseImage } from '@hospital/models';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { UserStore } from './UserStore';

interface IProps {
    show: boolean;
    store: UserStore;
    configuration: ClientConfigurationModel;
    existingLicense?: ILicenseModel;
    onCancel: () => void;
    onSubmit: (formData: ILicenseModelWithNewLicenseImage) => void;
}

export const UserProfileLicenseAddUpdate = observer(({ show, store, configuration, onSubmit, onCancel, existingLicense }: IProps) => {
    const { reset, register, handleSubmit, control, formState: { errors } } = useForm<ILicenseModelWithNewLicenseImage>({
        defaultValues: spreadAndtransformDefaultValues(existingLicense  as ILicenseModelWithNewLicenseImage),
        shouldFocusError: true
    });
    useEffect(() => {
        reset(spreadAndtransformDefaultValues(existingLicense));
    }, [reset, existingLicense]);

    const cancel = useCallback(() => {
        reset(spreadAndtransformDefaultValues(existingLicense));
        onCancel();
    }, [reset, onCancel, existingLicense])

    const editing = existingLicense && existingLicense.licenseId !== 0;
    return (
        <Modal show={show} data-test-id="modal-license-add" scrollable={false} backdrop="static" keyboard={false} onHide={cancel} size='xl' centered className="input-form" >
            <Modal.Header closeButton>
                <Modal.Title>Add New License</Modal.Title>
            </Modal.Header>
            <Modal.Body className="no-scroll">
                <Container fluid className="g-0">
                    <Form onSubmit={handleSubmit((data: ILicenseModelWithNewLicenseImage) => onSubmit(data))}>
                        <Row className="pt-4">
                            <Col md={4}>
                                <FormSelect label="License Type" control={control} fieldName='licenseType' highlightDirty={editing}
                                    options={store.licenseTypes}
                                    getOptionLabel={m => m.name}
                                    getOptionValue={m => m.licenseTypeId.toString()}
                                    mapOptionToValue={option => option}
                                    mapValueToOption={licenseType => licenseType}
                                />
                            </Col>
                            <Col md={4}>
                                <FormSelect label="State" control={control} fieldName='state' highlightDirty={editing}
                                    options={configuration.states}
                                    getOptionLabel={m => m.abbreviation}
                                    getOptionValue={m => m.stateId.toString()}
                                    mapOptionToValue={option => option}
                                    mapValueToOption={state => state}
                                />
                            </Col>
                            <Form.Group as={Col} md={4} controlId="licenseNumber">
                                <div className="me-md-2">
                                    <FormInput label="License Number" fieldName="number" control={control} highlightDirty={editing} rules={{
                                        required: "License Number is required",
                                        pattern: Patterns.alphaNum
                                    }} />
                                </div>

                            </Form.Group>
                        </Row>
                        <Row className="pt-4">
                            <Form.Group as={Col} md={4} controlId="expirationDate">
                                <Form.Label>Expiration Date</Form.Label>
                                <CustomDatePicker {...register("expirationDate", { required: { value: true, message: 'Expiration date is required' } })} />
                                <ErrorMessage
                                    errors={errors}
                                    name="expirationDate"
                                    render={({ message }) => <span className="text-danger">{message}</span>} />
                            </Form.Group>
                            <Form.Group as={Col} md={8} controlId="licensePhoto">
                                <div className="me-md-2">
                                    {!existingLicense?.licensePhoto &&
                                        <><FileUpload accept="image/*"
                                            labelText={existingLicense?.licensePhoto ? "Change License Image" : "New License Image"}
                                            isInvalid={errors.newLicensePhotoFile ? true : false}
                                            uploadButtonText="Select Image" {...register("newLicensePhotoFile")} />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.newLicensePhotoFile && <span role="alert">{errors.newLicensePhotoFile.message}</span>}
                                            </Form.Control.Feedback>
                                        </>
                                    }
                                    {existingLicense?.licensePhoto &&
                                        <>
                                            <Form.Label>CURRENT LICENSE IMAGE</Form.Label>
                                            <a className="form-control mb-md-2" href={existingLicense.licensePhoto.photoUrl}>{existingLicense.licensePhoto.fileName}</a>
                                            <FileUpload accept="image/*"
                                                uploadButtonText="Select Image"
                                                {...register("newLicensePhotoFile")}
                                                emptyText=""
                                            />

                                        </>
                                    }
                                </div>
                            </Form.Group>
                        </Row>
                        <Modal.Footer className="mt-3">
                            <Button
                                aria-label="cancel"
                                variant="light"
                                className="cancel"
                                type="button"
                                onClick={cancel}>Cancel</Button>
                            <Button
                                aria-label="submit"
                                className={store.licensesAreBeingUpdated ? 'button-loading align-items-end' : 'align-items-end'}
                                variant="primary"
                                type="submit"
                                disabled={store.licensesAreBeingUpdated}>{existingLicense ? "Update License" : "Save New License"}</Button>
                        </Modal.Footer>
                    </Form>
                </Container>
            </Modal.Body>
        </Modal>
    )
});
